import React, { useEffect, useState, useRef } from 'react';
import { RefreshCw, UserPlus, Users, Info, Trophy, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchAgentSignups } from '../api/analyticsApi';

/**
 * Leaderboard for the "Real Estate Agents" Traffic Sources row.
 *
 * Ranks agents by the buyer-portal activity attributed to their personal QR
 * (utm_source=real_estate_agent → ref_agent_id): sign-ups, visitors, conversion
 * rate, property views, enquiries and attributed commission. Top three get
 * medal styling. Designed to be embedded inside SourceDetailModal.
 */

// Gold / silver / bronze styling for the top three ranks.
const MEDALS = {
    0: { ring: '#F5B301', bg: '#FEF7E0', label: '🥇' },
    1: { ring: '#9CA3AF', bg: '#F3F4F6', label: '🥈' },
    2: { ring: '#CD7F32', bg: '#FBEDE3', label: '🥉' },
};

const fmtMoney = (n) => {
    const v = Number(n) || 0;
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
    return `$${v.toFixed(0)}`;
};

const initials = (name = '') =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('') || '?';

const StatCard = ({ icon, iconBg, iconColor, label, value, sub }) => (
    <div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-1.5">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg"
                style={{ background: iconBg, color: iconColor }}>
                {icon}
            </span>
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
);

const AgentSignupPanel = ({ startDate, endDate, accent = '#7C3AED' }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [helpOpen, setHelpOpen] = useState(false);
    const loadTokenRef = useRef(0);

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        fetchAgentSignups(startDate, endDate)
            .then((res) => { if (myToken === loadTokenRef.current) setData(res); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error('Agent signups load failed:', err);
                toast.error('Failed to load agent sign-ups');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                Loading agent leaderboard…
            </div>
        );
    }

    const byAgent = data?.by_agent || [];
    const totals = data?.totals || {};
    const total = data?.total ?? totals.signups ?? 0;
    const unattributed = data?.unattributed ?? 0;

    return (
        <div className="space-y-5">
            <button
                type="button"
                onClick={() => setHelpOpen((o) => !o)}
                className="w-full text-left rounded-lg px-3 py-2.5 flex gap-2 items-start border transition-colors hover:bg-gray-50"
                style={{ background: `${accent}08`, borderColor: `${accent}25` }}
            >
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: accent }} />
                <div className="min-w-0 flex-1">
                    <span className="text-xs font-medium text-gray-700">
                        How the agent leaderboard is tracked
                        <span className="text-gray-400 font-normal ml-1">{helpOpen ? '▲' : '▼'}</span>
                    </span>
                    {helpOpen && (
                        <p className="text-xs text-gray-600 leading-relaxed mt-1.5">
                            Each agent has a personal "QR Link to Portal" (Open Homes page) that
                            opens the buyer-portal tagged to them. Every buyer who arrives, signs up,
                            views properties, enquires or buys through that QR is credited to the
                            agent here. Conversion = sign-ups ÷ visitors; commission is the attributed
                            portal-sale GCI. Links are tagged{' '}
                            <code className="px-1 py-0.5 bg-white rounded border border-gray-200 text-[10px]">
                                ?utm_source=real_estate_agent&amp;agent_id=&lt;id&gt;
                            </code>.
                        </p>
                    )}
                </div>
            </button>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard
                    icon={<UserPlus className="w-3.5 h-3.5" />}
                    iconBg={`${accent}1a`} iconColor={accent}
                    label="Total sign-ups" value={total} sub="From agent QR codes"
                />
                <StatCard
                    icon={<Users className="w-3.5 h-3.5" />}
                    iconBg="#2563eb1a" iconColor="#2563eb"
                    label="Agents converting" value={byAgent.length}
                    sub={unattributed > 0 ? `${unattributed} without an agent` : 'with attributed activity'}
                />
                <StatCard
                    icon={<RefreshCw className="w-3.5 h-3.5" />}
                    iconBg="#0891b21a" iconColor="#0891b2"
                    label="Visitors" value={totals.visitors ?? 0}
                    sub={`${totals.conversion_rate ?? 0}% convert`}
                />
                <StatCard
                    icon={<DollarSign className="w-3.5 h-3.5" />}
                    iconBg="#15803d1a" iconColor="#15803d"
                    label="Attributed GCI" value={fmtMoney(totals.revenue_commission)}
                    sub={`${totals.attributed_sales ?? 0} sale${(totals.attributed_sales ?? 0) === 1 ? '' : 's'}`}
                />
            </div>

            {byAgent.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 p-10 text-center">
                    <Trophy className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700">No agent activity in this range yet</p>
                    <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
                        Once buyers arrive from an agent's "QR Link to Portal", they'll be ranked
                        here by sign-ups, conversion and attributed commission.
                    </p>
                </div>
            ) : (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <h4 className="text-sm font-semibold text-gray-900 px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                        <Trophy className="w-4 h-4" style={{ color: accent }} />
                        Agent leaderboard
                    </h4>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50/60 border-b border-gray-100">
                                    <th className="text-left font-medium px-3 py-2 w-10">#</th>
                                    <th className="text-left font-medium px-2 py-2">Agent</th>
                                    <th className="text-right font-medium px-2 py-2">Sign-ups</th>
                                    <th className="text-right font-medium px-2 py-2 hidden sm:table-cell">Visitors</th>
                                    <th className="text-right font-medium px-2 py-2">Conv.</th>
                                    <th className="text-right font-medium px-2 py-2 hidden sm:table-cell">Views</th>
                                    <th className="text-right font-medium px-2 py-2 hidden md:table-cell">Enquiries</th>
                                    <th className="text-right font-medium px-3 py-2">GCI</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {byAgent.map((row, i) => {
                                    const medal = MEDALS[i];
                                    return (
                                        <tr key={row.agent_id} className="hover:bg-gray-50/50">
                                            <td className="px-3 py-2.5">
                                                {medal ? (
                                                    <span className="text-base leading-none" title={`Rank ${i + 1}`}>{medal.label}</span>
                                                ) : (
                                                    <span className="text-xs font-semibold text-gray-400 tabular-nums">{i + 1}</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-2.5">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span
                                                        className="flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold flex-shrink-0"
                                                        style={medal
                                                            ? { background: medal.bg, color: medal.ring, boxShadow: `inset 0 0 0 1.5px ${medal.ring}` }
                                                            : { background: `${accent}15`, color: accent }}
                                                    >
                                                        {initials(row.agent_name)}
                                                    </span>
                                                    <span className="text-gray-800 font-medium truncate">{row.agent_name}</span>
                                                </div>
                                            </td>
                                            <td className="px-2 py-2.5 text-right font-semibold text-gray-900 tabular-nums">{row.signups}</td>
                                            <td className="px-2 py-2.5 text-right text-gray-600 tabular-nums hidden sm:table-cell">{row.visitors ?? 0}</td>
                                            <td className="px-2 py-2.5 text-right text-gray-600 tabular-nums">{row.conversion_rate ?? 0}%</td>
                                            <td className="px-2 py-2.5 text-right text-gray-600 tabular-nums hidden sm:table-cell">{row.properties_viewed ?? 0}</td>
                                            <td className="px-2 py-2.5 text-right text-gray-600 tabular-nums hidden md:table-cell">{row.enquiries ?? 0}</td>
                                            <td className="px-3 py-2.5 text-right font-semibold tabular-nums"
                                                style={{ color: (row.revenue_commission || 0) > 0 ? '#15803d' : '#9CA3AF' }}>
                                                {fmtMoney(row.revenue_commission)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentSignupPanel;
